/**
 * Piece labels that FOLLOW the panel edge.
 *
 * The size/order/player line used to be centred on the panel's lowest point
 * with a straight baseline. That only works when the bottom edge is a
 * horizontal line: on a diamond the two slanted edges leave the ends of the
 * text outside the fabric, and on a collar the lowest point is a tip with
 * nothing but air beside it, so the whole label floated outside the piece.
 *
 * Here the cut silhouette is inset a hair, flattened to a polygon, and the
 * label is laid glyph by glyph along the stretch of that polygon that sits
 * lowest on the piece while turning gently enough to stay readable. Glyph
 * "up" is the inward normal, so the letters always stand on fabric. When no
 * stretch can host the line, the font shrinks (to a floor) and then the
 * optional extras are dropped, size first kept.
 */
import ClipperLib from 'clipper-lib';
import { flattenPathData, IDENTITY } from './clipFlatten';
import type { GlyphFont } from './labelGlyphs';

export type Point = [number, number];
export type Polygon = Point[];

/** Clipper works on integers; a thousandth of a pt is far below cut precision. */
const SCALE = 1000;

/** Baseline sits this far inside the cut edge (≈0.5 mm). */
export const EDGE_LABEL_INSET_PT = 1.5;
/** Smallest label the factory can still read. */
export const EDGE_LABEL_MIN_FONT_PT = 10;
/** Total turning a label may follow (a diamond's 90° apex is rejected). */
const MAX_TOTAL_TURN_DEG = 50;
/** Turning between two adjacent glyph centres (keeps letters from colliding). */
const MAX_GLYPH_TURN_DEG = 18;
/**
 * Never upside-down: a glyph may lean up to vertical (a side edge) but its
 * tangent must not point left, or the line reads mirrored on the top edge.
 */
const MIN_TANGENT_X = -0.02;
/** Arc-length step when scanning candidate windows along the edge. */
const SCAN_STEP_PT = 3;

const round = (n: number) => Math.round(n * 1000) / 1000;

/**
 * The silhouette inset by `insetPt`, as flattened polygons sorted by area
 * (largest first). Null when the outline has no closed geometry.
 */
export async function insetOutline(
  outlineD: string,
  insetPt: number,
  tolerance = 0.2
): Promise<Polygon[] | null> {
  if (!outlineD) return null;
  const polylines = await flattenPathData(outlineD, IDENTITY, tolerance);
  if (!polylines?.length) return null;
  const subject = polylines
    .filter((p) => p.points.length >= 3)
    .map((p) => p.points.map(([x, y]) => ({ X: Math.round(x * SCALE), Y: Math.round(y * SCALE) })));
  if (!subject.length) return null;
  try {
    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(subject, ClipperLib.PolyType.ptSubject, true);
    const unioned: Array<Array<{ X: number; Y: number }>> = [];
    clipper.Execute(
      ClipperLib.ClipType.ctUnion,
      unioned,
      ClipperLib.PolyFillType.pftNonZero,
      ClipperLib.PolyFillType.pftNonZero
    );
    if (!unioned.length) return null;
    let solution: Array<Array<{ X: number; Y: number }>> = unioned;
    if (insetPt > 0) {
      const offsetter = new ClipperLib.ClipperOffset(2, tolerance * SCALE);
      offsetter.AddPaths(unioned, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
      solution = [];
      offsetter.Execute(solution, -insetPt * SCALE);
    }
    const polygons = solution
      .filter((p) => p.length >= 3)
      .map((p) => p.map((pt) => [pt.X / SCALE, pt.Y / SCALE] as Point));
    if (!polygons.length) return null;
    return polygons.sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)));
  } catch {
    return null;
  }
}

/**
 * Fabric area (pt²) enclosed by a cut silhouette — outer rings minus holes.
 * Null when the outline has no closed geometry.
 */
export async function outlineAreaPt2(outlineD: string): Promise<number | null> {
  const polygons = await insetOutline(outlineD, 0);
  if (!polygons?.length) return null;
  // Clipper's union returns outers and holes with opposite winding, so the
  // signed sum is the net fabric area.
  return Math.abs(polygons.reduce((sum, p) => sum + signedArea(p), 0));
}

export function signedArea(poly: Polygon): number {
  let a = 0;
  for (let i = 0, n = poly.length; i < n; i++) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % n];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

/** Ray-casting point-in-polygon (used by tests and by the fit check). */
export function pointInPolygon([px, py]: Point, poly: Polygon): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const crosses = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi || 1e-12) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

/** Closed polygon as an arc-length parametrised loop. */
class EdgeLoop {
  readonly pts: Polygon;
  readonly cum: number[] = [0];
  readonly length: number;

  constructor(poly: Polygon, walkForward: boolean) {
    this.pts = walkForward ? poly.slice() : poly.slice().reverse();
    for (let i = 0; i < this.pts.length; i++) {
      const a = this.pts[i];
      const b = this.pts[(i + 1) % this.pts.length];
      this.cum.push(this.cum[i] + Math.hypot(b[0] - a[0], b[1] - a[1]));
    }
    this.length = this.cum[this.pts.length];
  }

  /** Point and unit tangent at arc length s (wrapping). */
  at(s: number): { p: Point; t: Point } {
    const L = this.length;
    let u = ((s % L) + L) % L;
    // binary search the segment
    let lo = 0;
    let hi = this.pts.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (this.cum[mid] <= u) lo = mid;
      else hi = mid - 1;
    }
    const a = this.pts[lo];
    const b = this.pts[(lo + 1) % this.pts.length];
    const segLen = this.cum[lo + 1] - this.cum[lo] || 1e-9;
    const f = (u - this.cum[lo]) / segLen;
    const tx = (b[0] - a[0]) / segLen;
    const ty = (b[1] - a[1]) / segLen;
    return { p: [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f], t: [tx, ty] };
  }
}

/**
 * Walk direction such that the polygon interior is on the glyphs' "up" side
 * (up = tangent rotated so that at a bottom edge walked left→right it points
 * to −y): the interior must lie on the left of the walk in y-down screen
 * space, which is a NEGATIVE shoelace area for the walked order.
 */
function orientedLoop(poly: Polygon): EdgeLoop {
  return new EdgeLoop(poly, signedArea(poly) < 0);
}

export interface GlyphPlacement {
  char: string;
  x: number;
  y: number;
  /** Degrees, SVG rotate() convention. */
  angle: number;
  advance: number;
}

export interface EdgeLabelLayout {
  glyphs: GlyphPlacement[];
  fontSize: number;
  text: string;
}

function angleDiffDeg(a: Point, b: Point): number {
  const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1]));
  return (Math.acos(dot) * 180) / Math.PI;
}

/**
 * Best window of length `width` along the loop: lowest on the piece (largest
 * mean y) among windows whose turning stays within the limits. Null when no
 * window qualifies (label longer than the loop or the loop turns too much
 * everywhere).
 */
function bestWindowStart(loop: EdgeLoop, width: number, glyphStep: number): number | null {
  if (width > loop.length * 0.95) return null;
  const samples = Math.max(4, Math.ceil(width / Math.max(glyphStep, 1)));
  let best: { start: number; score: number } | null = null;
  for (let start = 0; start < loop.length; start += SCAN_STEP_PT) {
    let totalTurn = 0;
    let maxStepTurn = 0;
    let sumY = 0;
    let prevT: Point | null = null;
    let ok = true;
    for (let k = 0; k <= samples; k++) {
      const { p, t } = loop.at(start + (width * k) / samples);
      if (t[0] < MIN_TANGENT_X) {
        ok = false;
        break;
      }
      sumY += p[1];
      if (prevT) {
        const d = angleDiffDeg(prevT, t);
        totalTurn += d;
        if (d > maxStepTurn) maxStepTurn = d;
        if (totalTurn > MAX_TOTAL_TURN_DEG || maxStepTurn > MAX_GLYPH_TURN_DEG) {
          ok = false;
          break;
        }
      }
      prevT = t;
    }
    if (!ok) continue;
    // Prefer low on the piece, then straighter.
    const score = sumY / (samples + 1) - totalTurn * 0.05;
    if (!best || score > best.score) best = { start, score };
  }
  return best ? best.start : null;
}

/**
 * Lay `text` along the polygon's best bottom stretch. Tries the requested
 * size, shrinks towards the floor, then drops trailing ` · `-separated
 * extras. Null only when even the first segment at the floor size fits
 * nowhere.
 */
export function layoutTextAlongEdge(
  font: GlyphFont,
  polygon: Polygon,
  text: string,
  fontSize: number,
  minFontSize = EDGE_LABEL_MIN_FONT_PT
): EdgeLabelLayout | null {
  const line = text.toUpperCase().trim();
  if (!line || polygon.length < 3) return null;
  const loop = orientedLoop(polygon);
  const parts = line.split(' · ');

  for (let keep = parts.length; keep >= 1; keep--) {
    const candidate = parts.slice(0, keep).join(' · ');
    for (let size = fontSize; size >= minFontSize - 1e-9; size = round(size * 0.9)) {
      const chars = Array.from(candidate);
      const advances = chars.map((c) => font.getAdvanceWidth(c, size));
      const width = advances.reduce((a, b) => a + b, 0);
      const meanAdvance = width / Math.max(chars.length, 1);
      const start = bestWindowStart(loop, width, meanAdvance);
      if (start === null) {
        if (size === minFontSize) break;
        continue;
      }
      const glyphs: GlyphPlacement[] = [];
      let s = start;
      chars.forEach((char, i) => {
        const adv = advances[i];
        const { p, t } = loop.at(s + adv / 2);
        glyphs.push({
          char,
          x: p[0],
          y: p[1],
          angle: (Math.atan2(t[1], t[0]) * 180) / Math.PI,
          advance: adv,
        });
        s += adv;
      });
      return { glyphs, fontSize: size, text: candidate };
    }
  }
  return null;
}

/**
 * SVG markup for a laid-out label: every glyph as an outline path, white
 * stroke copy under a black fill copy (CorelDRAW keeps curves inside the
 * panel's PowerClip; it would drop `<text>`).
 */
export function edgeLabelMarkup(
  font: GlyphFont,
  layout: EdgeLabelLayout,
  strokeWidth: number
): string | null {
  const groups: string[] = [];
  for (const g of layout.glyphs) {
    if (g.char === ' ') continue;
    let d = '';
    try {
      d = font.getPath(g.char, -g.advance / 2, 0, layout.fontSize).toPathData(3);
    } catch {
      d = '';
    }
    if (!d) continue;
    groups.push(
      `<path transform="translate(${round(g.x)} ${round(g.y)}) rotate(${round(g.angle)})" d="${d}"/>`
    );
  }
  if (!groups.length) return null;
  const body = groups.join('');
  return (
    `<g fill="none" stroke="#ffffff" stroke-width="${round(strokeWidth)}" stroke-linejoin="round">${body}</g>` +
    `<g fill="#000000">${body}</g>`
  );
}

/**
 * One call for the sheet builder: inset the silhouette, lay the line along
 * its best bottom stretch, return the markup — or null so the caller can
 * fall back to the straight centred label.
 */
export async function edgeLabelForOutline(
  font: GlyphFont,
  outlineD: string,
  text: string,
  fontSize: number,
  strokeWidth: number
): Promise<{ markup: string; layout: EdgeLabelLayout } | null> {
  const polygons = await insetOutline(outlineD, EDGE_LABEL_INSET_PT);
  if (!polygons?.length) return null;
  const layout = layoutTextAlongEdge(font, polygons[0], text, fontSize);
  if (!layout) return null;
  const markup = edgeLabelMarkup(font, layout, strokeWidth);
  return markup ? { markup, layout } : null;
}
